import React, { useEffect, useMemo, useState } from 'react'
import { Link, useLocation, useNavigate, useOutletContext, useSearchParams } from 'react-router-dom'
import { getThemeById } from '../../config/themes'
import { withApiBaseUrl } from '../../lib/apiBaseUrl'
import { lotteryService, type LotteryRound } from '../../services/lotteryService'
import {
  formatYeekeeOpenTime,
  formatYeekeeShotTime,
  getYeekeeResultFromRound,
  getYeekeeRoundNumber,
  getYeekeeShotMemberName,
  type YeekeeShotRow,
} from '../../utils/yeekeeResultUtils'
import type { LobbyHuayOutletContext } from './ModernLobbyHuayLayout'
import './ModernLobbyHuayYeekeeResultPage.css'

const IMG_PLACEHOLDER = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7'

/** หน้ารายละเอียดผลยี่กีรอบ — แสดงผลรางวัล + ตารางยิงเลข (เทียบ user-classic /yeekee-result) */
const ModernLobbyHuayYeekeeResultPage: React.FC = () => {
  const { themeConfig, assetConfig } = useOutletContext<LobbyHuayOutletContext>()
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const tenantId = themeConfig.tenantId

  const stateRound = (location.state as { round?: LotteryRound } | null)?.round
  const roundIdFromQuery = searchParams.get('roundId')

  const [round, setRound] = useState<LotteryRound | undefined>(stateRound)
  const [loading, setLoading] = useState(!stateRound)
  const [shots, setShots] = useState<YeekeeShotRow[]>([])
  const [loadingShots, setLoadingShots] = useState(false)

  const themeFolder = useMemo(
    () => getThemeById(themeConfig.themeId)?.folder ?? 'HuayToday-Blue',
    [themeConfig.themeId],
  )
  const getAssetUrl = (path: string) => {
    const base = assetConfig.CDN_BASE_URL ? `${assetConfig.CDN_BASE_URL}/${themeFolder}` : `/${themeFolder}`
    return base + (path.startsWith('/') ? path : `/${path}`)
  }

  const roundId = round?._id || stateRound?._id || roundIdFromQuery || ''
  const result = getYeekeeResultFromRound(round) ?? getYeekeeResultFromRound(stateRound)
  const roundNum = getYeekeeRoundNumber(round ?? stateRound)
  const openTimeStr = formatYeekeeOpenTime(round ?? stateRound)

  useEffect(() => {
    const initialRound = stateRound
    if (initialRound) {
      setRound(initialRound)
      setLoading(false)
      lotteryService
        .getRoundById(initialRound._id)
        .then((r) => {
          if (r) setRound(r)
        })
        .catch(() => {})
      return
    }

    if (roundIdFromQuery) {
      setLoading(true)
      lotteryService
        .getRoundById(roundIdFromQuery)
        .then((r) => {
          if (r) setRound(r)
        })
        .finally(() => setLoading(false))
      return
    }

    setLoading(false)
  }, [stateRound, roundIdFromQuery])

  useEffect(() => {
    if (!roundId || !tenantId) return
    let cancelled = false
    setLoadingShots(true)

    const token = localStorage.getItem('accessToken')
    fetch(withApiBaseUrl(`/api/v1/lottery/tenant/${tenantId}/yeekee/rounds/${roundId}/shots`), {
      headers: {
        'Content-Type': 'application/json',
        'X-Tenant-ID': tenantId,
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    })
      .then((res) => res.json())
      .then((data) => {
        if (!cancelled && data?.success && Array.isArray(data.shots)) {
          setShots(data.shots)
        }
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoadingShots(false)
      })

    return () => {
      cancelled = true
    }
  }, [roundId, tenantId])

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status" />
        <p className="mt-2 c_tertiary">กำลังโหลดผลรอบยี่กี...</p>
      </div>
    )
  }

  if (!round) {
    return (
      <>
        <div className="lobby-huay-page-header c_quinary">
          <Link to="/lobby-huay/result" className="lobby-huay-page-header__back">
            <i className="fas fa-chevron-left" aria-hidden="true" />
            <span>กลับผลหวย</span>
          </Link>
          <h3 className="lobby-huay-page-header__title c_tertiary font_md">ผลยี่กี</h3>
        </div>
        <div className="text-center py-5 c_secondary">
          <p>ไม่พบข้อมูลรอบ</p>
          <button type="button" className="button_link mt-2" onClick={() => navigate('/lobby-huay/result')}>
            กลับหน้าผลหวย
          </button>
        </div>
      </>
    )
  }

  const threeUp = result?.threeDigitTop || '-'
  const twoDown = result?.twoDigitDown ?? result?.twoDigitTop ?? '-'
  const sumTotal = result?.sumTotal != null ? String(result.sumTotal) : null

  return (
    <div className="yeekee-result-page">
      <div className="lobby-huay-page-header c_quinary">
        <Link to="/lobby-huay/result" className="lobby-huay-page-header__back">
          <i className="fas fa-chevron-left" aria-hidden="true" />
          <span>กลับผลหวย</span>
        </Link>
        <h3 className="lobby-huay-page-header__title c_tertiary font_md">ผลยี่กี VIP</h3>
      </div>

      <div className="row mx__1 mt-2">
        <div className="col-12 px-1">
          <div className="yeekee-result-hero">
            <div className="yeekee-result-hero__top">
              <div className="yeekee-result-hero__brand">
                <img
                  src={getAssetUrl('/img/yk.jpg')}
                  alt=""
                  onError={(e) => {
                    e.currentTarget.src = IMG_PLACEHOLDER
                  }}
                />
                <div className="min-w-0">
                  <h5 className="c_tertiary mb-0">หวยยี่กี VIP</h5>
                  <p className="mb-0 small c_secondary">รอบที่ {roundNum}</p>
                </div>
              </div>
              <div className="d-flex flex-column align-items-end gap-1">
                <div className="yeekee-result-hero__badge c_tertiary small">
                  {openTimeStr || `รอบที่ ${roundNum}`}
                </div>
                <button
                  type="button"
                  className="btn btn-sm border-0 px-2 py-1"
                  style={{
                    borderRadius: 999,
                    background: 'rgba(74, 210, 255, 0.14)',
                    color: '#bae6fd',
                    fontSize: '0.78rem',
                  }}
                  onClick={() =>
                    navigate('/lobby-huay/lottery-terms', { state: { round, lotteryType: 'yeekee' as const } })
                  }
                >
                  กติกา & วิธีเล่น
                </button>
              </div>
            </div>

            <div className="yeekee-result-metrics">
              <div className="yeekee-result-metric">
                <div className="yeekee-result-metric__label">3 ตัวบน</div>
                <div className="yeekee-result-metric__value">{threeUp}</div>
              </div>
              <div className="yeekee-result-metric">
                <div className="yeekee-result-metric__label">2 ตัวล่าง</div>
                <div className="yeekee-result-metric__value">{twoDown}</div>
              </div>
            </div>

            <div className="yeekee-result-sum-panel">
              <h5 className="c_tertiary text-center">ผลรวมเลขและสมาชิกที่ร่วมออกรางวัล</h5>
              <div className="yeekee-result-sum-panel__value text-center">{sumTotal ?? '-'}</div>
              {!result && (
                <p className="text-center c_secondary small mb-2">
                  รอผลการออกรางวัล (ระบบจะคำนวณเมื่อครบ 25 ชุด)
                </p>
              )}
              {result?.sumTotal != null && result.position16Number != null && result.finalNumber != null && (
                <p className="yeekee-result-formula text-center">
                  ผลรอบนี้: <strong>{result.sumTotal}</strong> − <strong>{result.position16Number}</strong> ={' '}
                  <strong>{result.finalNumber}</strong> → 3 ตัวบน ({result.threeDigitTop}) 2 ตัวล่าง (
                  {result.twoDigitDown ?? result.twoDigitTop ?? '-'})
                </p>
              )}

              {loadingShots ? (
                <p className="text-center c_secondary small mb-0">
                  <i className="fas fa-spinner fa-spin me-1" />
                  กำลังโหลดรายการยิงเลข...
                </p>
              ) : (
                <div className="yeekee-result-table-wrap mt-2">
                  <table className="table table-striped text-center mb-0">
                    <thead>
                      <tr>
                        <th>ลำดับ</th>
                        <th>เลข</th>
                        <th>สมาชิก</th>
                        <th>เวลา</th>
                      </tr>
                    </thead>
                    <tbody>
                      {shots.length > 0 ? (
                        shots.map((shot, index) => {
                          const rowClass =
                            index === 4
                              ? 'yeekee-result-row--bonus-5'
                              : index === 15
                                ? 'yeekee-result-row--bonus-16'
                                : ''
                          return (
                            <tr key={`${shot.number}-${index}`} className={rowClass}>
                              <td>
                                <span className="yeekee-result-rank-pill">{index + 1}</span>
                              </td>
                              <td>{shot.number}</td>
                              <td>{getYeekeeShotMemberName(shot, index)}</td>
                              <td>{formatYeekeeShotTime(shot.shotAt)}</td>
                            </tr>
                          )
                        })
                      ) : (
                        <tr>
                          <td colSpan={4} className="py-3 c_secondary">
                            ยังไม่มีรายการยิงเลข
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="yeekee-result-bonus-grid">
              <div className="yeekee-result-bonus-card yeekee-result-bonus-card--five">
                <small>ยิงลำดับที่ 5 (โบนัส 200)</small>
                <h5 className="c_tertiary">{shots[4] ? getYeekeeShotMemberName(shots[4], 4) : '-'}</h5>
              </div>
              <div className="yeekee-result-bonus-card yeekee-result-bonus-card--sixteen">
                <small>ยิงลำดับที่ 16 (โบนัส 400)</small>
                <h5 className="c_tertiary">{shots[15] ? getYeekeeShotMemberName(shots[15], 15) : '-'}</h5>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ModernLobbyHuayYeekeeResultPage
